import { useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Box, Button, Stack } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {
  hasRiskAssessment,
  selectedStructureRiskAssessment,
  snackbarMessage as snackbarMessageState,
} from '@/components/Analysis/state';
import { RiskAssessmentDataLoader } from './RiskAssessmentDataLoader/RiskAssessmentDataLoader';
import { RiskAssessmentContainer } from './RiskAssessmentContainer/RiskAssessmentContainer';
import { StructureSetup } from './StructureSetup';
import { FeedbackCircle } from '@/components/shared/FeedbackCircle';
import { Dialog } from '@/components/shared/Dialog';
import { StructureCheck } from '@/components/shared/StructureCheck';
import { PageLoader } from '@/components/shared/PageLoader';
import { Snackbar } from '@/components/shared/Snackbar/Snackbar';
import { AccessGuard } from '@/components/Analysis/shared/AccessGuard';
import { StructureMenu } from './StructureMenu';
import { EsSheetSummary } from './Summary';
import { EsSheetStatus } from '@/__generated__/graphql';
import { riskView } from './state/atoms';
import { useRouter } from '@/hooks';
import { ActionButton, SummaryContainer } from './shared/styles';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useGenerateDataForXlsx } from './hooks/useGenerateDataForXlsx';
import { containedButtonStyle } from '@/theme';

type View = 'calculations' | 'summary' | undefined;

interface RiskAssessmentProps {
  structureId: string;
  view: View;
}

export const RiskAssessment = ({ structureId, view: pageView }: RiskAssessmentProps) => {
  const structureHasRiskAssessment = useAtomValue(hasRiskAssessment);
  const structure = useAtomValue(selectedStructureRiskAssessment);
  const [view, setView] = useAtom(riskView);

  const [snackbarMessage, setSnackbarMessage] = useAtom(snackbarMessageState);

  const [showPlatformSetup, setShowPlatformSetup] = useState(false);
  const [platformSetupLoading, setPlatformSetupLoading] = useState(false);
  const [showVersionSidebar, setShowVersionSidebar] = useState(false);
  const [showEquipmentDrawer, setShowEquipmentDrawer] = useState(false);
  const router = useRouter();
  const navbarHeight = useNavbarHeight();
  const { rightDrawerWidth } = useDrawerWidths();

  useEffect(() => {
    setView(pageView);
  }, [pageView, setView]);

  const handleClickPlatformSetup = () => setShowPlatformSetup((current) => !current);

  const handleClose = () => setShowPlatformSetup(false);

  const { isEsSheetPublished, hasRiskAssessmentData, riskAssessmentDone } = useMemo(() => {
    const { esSheetStatus, templateData, isLegacyEquipmentStrategy } = structure ?? {};
    const isRiskAssessmentStructure = Array.isArray(templateData) && templateData.length > 0;

    return {
      isEsSheetPublished: !!esSheetStatus && esSheetStatus === EsSheetStatus.Published,
      hasRiskAssessmentData: isRiskAssessmentStructure,
      riskAssessmentDone:
        isLegacyEquipmentStrategy ||
        (isRiskAssessmentStructure && templateData.every((item) => item.value !== '')),
    };
  }, [structure]);

  const handleBackToSummary = () => {
    const currentQuery = { ...router.query, view: 'summary' };
    router.push({
      pathname: router.pathname,
      query: currentQuery,
    });
    setView('summary');
  };

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };
  const { shouldShow, content } = snackbarMessage;

  const { downloadXlsxFile, xlsxLoading } = useGenerateDataForXlsx();

  return (
    <StructureCheck structureId={structureId}>
      <Snackbar open={shouldShow} onClose={closeSnack}>
        {content}
      </Snackbar>
      <AccessGuard structureId={structureId} feature="riskAssessment">
        {platformSetupLoading && (
          <Stack
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: 0,
              zIndex: 1051,
            }}
          >
            <PageLoader hideProgress backdrop background="dark" />
          </Stack>
        )}
        <RiskAssessmentDataLoader structureId={structureId}>
          {structureHasRiskAssessment && view === 'calculations' && (
            <Stack
              alignItems="flex-start"
              sx={{
                padding: '3.2rem 6rem',
                paddingBottom: 0,
                overflowY: 'auto',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                height: `calc(100vh - ${navbarHeight}px)`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width={
                  showEquipmentDrawer || showVersionSidebar
                    ? `calc(100% - ${rightDrawerWidth}px)`
                    : '100%'
                }
                mb={'1.6rem'}
              >
                <StructureMenu
                  structureName={structure?.name ?? ''}
                  showVersion={showVersionSidebar}
                  handleToggle={() => {
                    setShowVersionSidebar(!showVersionSidebar);
                  }}
                />
                <Stack direction="row" spacing={2}>
                  <ActionButton onClick={handleBackToSummary}>
                    <AbyssIcon iconName="undo" style={{ width: '1.6rem', height: '1.6rem' }} />
                    <span>Summary Sheet</span>
                  </ActionButton>
                  {hasRiskAssessmentData && !isEsSheetPublished && (
                    <Stack>
                      <ActionButton
                        contained
                        onClick={handleClickPlatformSetup}
                        sx={{ containedButtonStyle }}
                      >
                        <AbyssIcon iconName="build" style={{ width: '1.6rem', height: '1.6rem' }} />
                        <span>Asset Setup</span>
                      </ActionButton>
                    </Stack>
                  )}
                  <Stack>
                    <ActionButton contained onClick={() => downloadXlsxFile()}>
                      <AbyssIcon
                        iconName="file-download-line"
                        style={{
                          width: '1.6rem',
                          height: '1.6rem',
                          filter: 'brightness(0) invert(1)',
                        }}
                      />
                      <span>{xlsxLoading ? 'Exporting...' : 'Export ES Sheet'}</span>
                    </ActionButton>
                  </Stack>
                </Stack>
              </Stack>
              <Box width="calc(100vw - 96px)">
                {hasRiskAssessmentData ? (
                  <RiskAssessmentContainer
                    showVersionSidebar={showVersionSidebar}
                    isEsSheetPublished={isEsSheetPublished}
                    setShowVersionSidebar={setShowVersionSidebar}
                    setShowEquipmentDrawer={setShowEquipmentDrawer}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FeedbackCircle
                      text="You have not set up any platform yet"
                      actionButton={
                        <Button
                          variant="contained"
                          startIcon={<AddCircleIcon />}
                          onClick={handleClickPlatformSetup}
                        >
                          Set up asset now
                        </Button>
                      }
                    />
                  </Box>
                )}
              </Box>
            </Stack>
          )}
          {structureHasRiskAssessment && view === 'summary' && (
            <SummaryContainer>
              <EsSheetSummary
                structure={structure}
                published={isEsSheetPublished}
                riskAssessmentDone={riskAssessmentDone}
                handleClickPlatformSetup={handleClickPlatformSetup}
              />
            </SummaryContainer>
          )}
        </RiskAssessmentDataLoader>

        {structure && (
          <Dialog
            maxWidth="sm"
            title="Asset Setup"
            fullWidth
            onClose={handleClose}
            open={showPlatformSetup && !platformSetupLoading}
          >
            <StructureSetup
              onSubmit={handleClose}
              onCancel={handleClose}
              setLoader={setPlatformSetupLoading}
            />
          </Dialog>
        )}
      </AccessGuard>
    </StructureCheck>
  );
};
